import React, { useState } from "react";
import {
  Modal,
  Button,
  Input,
  Upload,
  Alert,
  Tag,
  Typography,
  Space,
  notification,
} from "antd";
import { postReq } from "../api";

const { TextArea } = Input;
const { Text } = Typography;

/**
 * "Fill with AI" — reads a pasted requirement (or an uploaded JD file) and
 * pre-fills the Create Requirement form.
 *
 * Backed by POST /{company|common}/requirement/ai-parse, which is READ-ONLY:
 * it creates nothing. This component only calls form.setFieldsValue(), so the
 * recruiter still reviews everything and submits through the normal Submit
 * button. Nothing about saving a requirement changes.
 */

const ACCEPTED = ".pdf,.docx,.txt,.md";

/**
 * Maps the API response onto the antd form's field types.
 *
 * This mapping is not cosmetic — three real mismatches have to be handled or
 * the fields silently fail validation on submit:
 *
 *  1. minExperience / maxExperience are InputNumber with `type: "number"`
 *     validators, but the API returns STRINGS ("5") because that is how
 *     requirementModel stores them. Passing the string through leaves the field
 *     looking filled while failing validation.
 *  2. The form's experience rules cap min at 15 and max at 18, so a JD asking
 *     for 20 years would be un-submittable. Clamped, and reported.
 *  3. `requiredTechnology` has no field in this form at all (it is unused in
 *     all 506 existing requirements), so whatever the agent resolved for it is
 *     intentionally dropped rather than written somewhere invisible.
 */
const toFormValues = (fields) => {
  const num = (v) => {
    if (v === null || v === undefined || v === "") return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  const clamp = (n, lo, hi) =>
    n === undefined ? undefined : Math.min(Math.max(n, lo), hi);

  const values = {};
  const clamped = [];

  if (fields.requirementName) {
    values.requirementName = fields.requirementName;
    // The Requirement Name input carries maxLength={20}. That only limits
    // TYPING, not setFieldsValue, and there is no length validation rule — so a
    // longer title sets and submits fine. It is left intact rather than
    // truncated ("Sr. React JS Developer" → "Sr. React JS Develop" helps
    // nobody), but flagged, because the user will not be able to lengthen it by
    // hand if they edit the field.
    if (fields.requirementName.length > 20) {
      clamped.push(
        `"${fields.requirementName}" is ${fields.requirementName.length} characters — longer than this field normally allows (20). Shorten it if you want to edit it by hand.`,
      );
    }
  }
  if (fields.requirementJobDescription)
    values.requirementJobDescription = fields.requirementJobDescription;
  if (fields.clientId) values.clientId = fields.clientId;
  if (fields.requirementType) values.requirementType = fields.requirementType;
  if (fields.jobLocation) values.jobLocation = fields.jobLocation;
  if (fields.workType) values.workType = fields.workType;

  if (Array.isArray(fields.requirementNiche) && fields.requirementNiche.length) {
    // The form's Select uses `${data._id}` as its option values, so these must
    // be strings to match — the API already returns them stringified.
    values.requirementNiche = fields.requirementNiche.map(String);
  }

  const budget = num(fields.budget);
  if (budget !== undefined) values.budget = budget;

  const vacancy = clamp(num(fields.vacancy), 1, 999);
  if (vacancy !== undefined) values.vacancy = vacancy;

  const rawMin = num(fields.minExperience);
  const min = clamp(rawMin, 1, 15);
  if (min !== undefined) {
    values.minExperience = min;
    if (rawMin !== min)
      clamped.push(`Min experience adjusted ${rawMin} → ${min} to fit this form's limit.`);
  }

  const rawMax = num(fields.maxExperience);
  const max = clamp(rawMax, 1, 18);
  if (max !== undefined) {
    values.maxExperience = max;
    if (rawMax !== max)
      clamped.push(`Max experience adjusted ${rawMax} → ${max} to fit this form's limit.`);
  }

  return { values, clamped };
};

// Field name → the label the recruiter sees, so messages match the form.
const LABELS = {
  requirementName: "Requirement Name",
  requirementJobDescription: "Job Description",
  clientId: "Client",
  requirementType: "Requirement Type",
  requirementNiche: "Technology Niche",
  requiredTechnology: "Required Technology",
  budget: "Budget",
  budgetRangeLower: "Budget (lower)",
  budgetRangeHigher: "Budget (upper)",
  minExperience: "Min. Experience",
  maxExperience: "Max. Experience",
  jobLocation: "Job Location",
  workType: "Work Status",
  vacancy: "Vacancy",
};

// Fields this form does not have. Never reported as "missing" — telling a user
// to fill a field that isn't on screen is just confusing.
const NOT_ON_FORM = new Set([
  "requiredTechnology",
  "budgetRangeLower",
  "budgetRangeHigher",
]);

export default function AiParseRequirement({ form, userRole, permissions = [] }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  // Mirrors the endpoint choice in Requirements.js handleSubmit — a company
  // login and a permissioned user hit different route trees.
  const endpoint =
    userRole === "company"
      ? "/company/requirement/ai-parse"
      : "/common/requirement/ai-parse";

  const reset = () => {
    setText("");
    setFile(null);
    setResult(null);
    setLoading(false);
  };

  const close = () => {
    setOpen(false);
    reset();
  };

  const handleParse = async () => {
    if (!text.trim() && !file) {
      notification.warning({
        message: "Nothing to read",
        description: "Paste the requirement, or attach a JD file.",
        placement: "topRight",
      });
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      let res;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        if (text.trim()) fd.append("text", text);
        // Content-Type is left unset on purpose so axios adds the multipart
        // boundary itself; setting it by hand produces a malformed request.
        res = await postReq(endpoint, fd);
      } else {
        res = await postReq(endpoint, { text });
      }

      if (!res || res.status !== 1) {
        notification.error({
          message: "Could not read the requirement",
          description: res?.message || "Please try again.",
          placement: "topRight",
        });
        return;
      }

      const data = res.data;

      if (!data.isRequirement) {
        notification.warning({
          message: "This doesn't look like a requirement",
          description:
            (data.reason || "") +
            " — you can still edit the text and try again, or fill the form manually.",
          placement: "topRight",
        });
        return;
      }

      const { values, clamped } = toFormValues(data.fields);
      form.setFieldsValue(values);

      setResult({
        filled: Object.keys(values),
        missing: (data.missingFields || []).filter((f) => !NOT_ON_FORM.has(f)),
        unmatched: data.unmatchedTerms || [],
        client: data.resolved?.client,
        niche: data.resolved?.niche || [],
        // Other roles found in the same text. A form holds one requirement, so
        // these are surfaced rather than silently discarded — the recruiter
        // pastes them separately.
        additional: data.additionalRequirements || [],
        notes: data.notes || [],
        confidence: data.fieldConfidence || {},
        clamped,
      });

      notification.success({
        message: `Filled ${Object.keys(values).length} field(s)`,
        description: "Check the values below, then Submit as usual.",
        placement: "topRight",
      });
    } catch (err) {
      notification.error({
        message: "Could not reach the AI service",
        description: err?.message || "Please try again, or fill the form manually.",
        placement: "topRight",
      });
    } finally {
      setLoading(false);
    }
  };

  const lowConfidence = result
    ? result.filled.filter((f) => (result.confidence[f] ?? 1) < 0.7)
    : [];

  return (
    <>
      <Button
        type="dashed"
        onClick={() => setOpen(true)}
        style={{ borderColor: "#3699FF", color: "#3699FF" }}
      >
        ✨ Fill with AI
      </Button>

      <Modal
        title={
          <span style={{ color: "#13487D", fontWeight: 700 }}>
            Fill the form from a requirement
          </span>
        }
        open={open}
        onCancel={close}
        width={680}
        footer={[
          <Button key="close" onClick={close}>
            Close
          </Button>,
          <Button
            key="parse"
            type="primary"
            loading={loading}
            onClick={handleParse}
            style={{ background: "#3699FF", borderColor: "#3699FF" }}
          >
            {result ? "Read again" : "Read & fill form"}
          </Button>,
        ]}
        destroyOnClose
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          Paste the requirement exactly as the client sent it — an email, a chat
          message, rough notes. It does not need to be tidy. Nothing is saved
          until you press Submit on the form.
        </Text>

        <TextArea
          rows={9}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={
            "e.g.\n\nHi, we need 2 Sr. React developers for Trovec Systems.\nExp 5-8 yrs, budget 8-12 LPA, Pune hybrid.\nSkills: React, Node.js, MongoDB. C2H position, immediate joiners."
          }
          style={{ marginTop: 12 }}
          disabled={loading}
        />

        <Space style={{ marginTop: 12 }} wrap>
          <Upload
            accept={ACCEPTED}
            maxCount={1}
            beforeUpload={(f) => {
              setFile(f);
              return false; // keep it client-side; we send it ourselves
            }}
            onRemove={() => setFile(null)}
            fileList={file ? [file] : []}
            disabled={loading}
          >
            <Button size="small">Attach JD file</Button>
          </Upload>
          <Text type="secondary" style={{ fontSize: 12 }}>
            PDF, DOCX, TXT — optional
          </Text>
        </Space>

        {result && (
          <div style={{ marginTop: 16 }}>
            <Alert
              type="success"
              showIcon
              message={`Filled ${result.filled.length} field(s) on the form`}
              description={
                <div style={{ fontSize: 12 }}>
                  {result.client?.name && (
                    <div style={{ marginBottom: 6 }}>
                      Client: <strong>{result.client.name}</strong>
                      {result.client.extractedName &&
                        ` (read as "${result.client.extractedName}")`}
                    </div>
                  )}
                  {!result.client?.name && result.client?.extractedName && (
                    <div style={{ marginBottom: 6 }}>
                      Could not match client{" "}
                      <strong>"{result.client.extractedName}"</strong> — please
                      pick it yourself.
                    </div>
                  )}
                  {result.niche.length > 0 && (
                    <div style={{ marginBottom: 6 }}>
                      Niche:{" "}
                      {result.niche.map((n) => (
                        <Tag key={n._id} color="blue">
                          {n.label}
                        </Tag>
                      ))}
                    </div>
                  )}
                </div>
              }
            />

            {result.additional.length > 0 && (
              <Alert
                style={{ marginTop: 10 }}
                type="warning"
                showIcon
                message={`This text contains ${result.additional.length + 1} requirements — the form is filled with the first`}
                description={
                  <div style={{ fontSize: 12 }}>
                    <div style={{ marginBottom: 4 }}>
                      Submit this one, then paste the others separately:
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {result.additional.map((a, i) => (
                        <li key={i}>
                          <strong>{a.requirementName || "(untitled)"}</strong>
                          {a.jobLocation ? ` · ${a.jobLocation}` : ""}
                          {a.minExperience
                            ? ` · ${a.minExperience}${a.maxExperience ? `-${a.maxExperience}` : "+"} yrs`
                            : ""}
                          {a.budget ? ` · ₹${Number(a.budget).toLocaleString("en-IN")}/mo` : ""}
                        </li>
                      ))}
                    </ul>
                  </div>
                }
              />
            )}

            {lowConfidence.length > 0 && (
              <Alert
                style={{ marginTop: 10 }}
                type="warning"
                showIcon
                message="Double-check these — the AI wasn't confident"
                description={
                  <span style={{ fontSize: 12 }}>
                    {lowConfidence.map((f) => LABELS[f] || f).join(", ")}
                  </span>
                }
              />
            )}

            {result.missing.length > 0 && (
              <Alert
                style={{ marginTop: 10 }}
                type="info"
                showIcon
                message="Not stated in the text — please fill these in"
                description={
                  <span style={{ fontSize: 12 }}>
                    {result.missing.map((f) => LABELS[f] || f).join(", ")}
                  </span>
                }
              />
            )}

            {result.unmatched.length > 0 && (
              <Alert
                style={{ marginTop: 10 }}
                type="info"
                showIcon
                message="Skills with no matching tag"
                description={
                  <span style={{ fontSize: 12 }}>
                    {result.unmatched.join(", ")} — add them as tags first if you
                    want them on this requirement.
                  </span>
                }
              />
            )}

            {(result.notes.length > 0 || result.clamped.length > 0) && (
              <Alert
                style={{ marginTop: 10 }}
                type="warning"
                showIcon
                message="Notes"
                description={
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
                    {result.notes.map((n, i) => (
                      <li key={`n${i}`}>{n}</li>
                    ))}
                    {result.clamped.map((c, i) => (
                      <li key={`c${i}`}>{c}</li>
                    ))}
                  </ul>
                }
              />
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
